/**
 * Chat Services Index
 * Re-exports all chat services for backward compatibility
 * 
 * NOTE: Block operations now delegate to unified blockService
 */

import { chatRoomService, ChatRoomWithDetails } from './chatRoomService';
import { chatMessageService, ChatMessageWithSender, MAX_FILE_SIZE } from './chatMessageService';
import { blockService } from '@/lib/services/blockService';
import { config } from '@/lib/config/environment';

// Re-export types
export type { ChatRoomWithDetails } from './chatRoomService';
export type { ChatMessageWithSender } from './chatMessageService';

// Re-export individual services
export { chatRoomService } from './chatRoomService';
export { chatMessageService } from './chatMessageService';

// Re-export blockService for direct access
export { blockService } from '@/lib/services/blockService';

/**
 * Combined chat service for backward compatibility
 * Aggregates all chat service methods into a single object
 */
export const chatService = {
  // Room operations
  getChatRooms: chatRoomService.getChatRooms.bind(chatRoomService),
  getChatRoom: chatRoomService.getChatRoom.bind(chatRoomService),
  createChatRoomForApplication: chatRoomService.createChatRoomForApplication.bind(chatRoomService),
  createDirectChat: chatRoomService.createDirectChat.bind(chatRoomService),
  getTotalUnreadCount: chatRoomService.getTotalUnreadCount.bind(chatRoomService),

  // Message operations
  getChatMessages: chatMessageService.getChatMessages.bind(chatMessageService),
  sendMessage: chatMessageService.sendMessage.bind(chatMessageService),
  sendImageMessage: chatMessageService.sendImageMessage.bind(chatMessageService),
  markAsRead: chatMessageService.markAsRead.bind(chatMessageService),
  uploadAttachment: chatMessageService.uploadAttachment.bind(chatMessageService),
  validateFile: chatMessageService.validateFile.bind(chatMessageService),

  // Block operations (delegated to unified blockService)
  blockUser: async (blockedUserId: string) => {
    const result = await blockService.blockUser(blockedUserId);
    return { error: result.success ? null : new Error(result.error || 'Block failed') };
  },
  unblockUser: async (blockedUserId: string) => {
    const result = await blockService.unblockUser(blockedUserId);
    return { error: result.success ? null : new Error(result.error || 'Unblock failed') };
  },
  getBlockedUsers: blockService.getBlockedUsers,
  isBlockedBy: async (targetUserId: string) => {
    const result = await blockService.isBlockedBy(targetUserId);
    return { 
      blocked: result.status === 'blocked', 
      error: result.error ? new Error(result.error) : null 
    };
  },
  getBlockedChats: blockService.getBlockedChats,

  // File validation constants (from environment config)
  MAX_FILE_SIZE: config.uploads.chat.maxFileSize,
  ALLOWED_MIME_TYPES: [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ],
  BLOCKED_EXTENSIONS: [
    'exe', 'bat', 'cmd', 'sh', 'msi', 'dll', 'com', 'scr', 'ps1', 'vbs', 'js', 'jar', 'app', 'dmg', 'pkg', 'deb', 'rpm'
  ],
};

export default chatService;
